/**
 * Remark plugin that turns inline `<badge tone="…">label</badge>` markers
 * in streamed assistant text into tone-badge nodes at markdown render
 * time.
 *
 * Mirrors the citation-pills pattern. See
 * `tone-badge-markers.ts` for the marker grammar and the standalone
 * (unit-testable) helpers, and `upright-chat-tool-uis.tsx` for the
 * `Badge` React component the rendered link node resolves to.
 *
 * PLT-4896 / PLT-4633: also visit `html` mdast nodes. micromark /
 * `remarkParse` may tokenise a `<badge …>` directive as an `html` mdast
 * node rather than as part of the surrounding `text` node — typically
 * when the tag sits at the start of a soft-wrapped line, immediately
 * after a `**bold**` span, or with whitespace inside the tag. The
 * `text`-only visitor never sees it, and we don't enable `rehype-raw`
 * (raw HTML otherwise bleeds straight into the rendered DOM), so without
 * this second pass the literal `<badge …>` characters leak to the user.
 * The citation and lock-badge plugins gained this same pass in PLT-4896;
 * the lock plugin's own comment flagged that tone badges lacked the
 * fallthrough and to "revisit if drift telemetry shows the same shape
 * there" — PLT-4633 is that revisit. Recovery runs the same splitter on
 * `node.value`, so orphan-opener auto-close (PLT-4633) applies uniformly
 * to both passes.
 */
import { SKIP, visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Html, Root, Text } from 'mdast';
import {
  reconstructBadgeFromHtmlNodes,
  splitTextOnToneBadgeMarkers,
  type BadgeReconNode,
  type MarkerSplitChild,
} from './tone-badge-markers';

export const remarkToneBadges: Plugin<[], Root> = () => {
  return tree => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnToneBadgeMarkers(node.value);
      if (newChildren === null) return;
      // mdast spec allows `link` wherever `text` is allowed; the parent
      // children type is a strict union that doesn't include `link` for
      // every variant (e.g. table cells), so cast to keep TS happy. Same
      // pattern as the citation plugin.
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
    // Second pass (PLT-4896 / PLT-4633): tolerate html-tokenised badge
    // tags. A `<badge …>` directive micromark tokenises as an `html`
    // mdast node never reaches the `text` visitor; without this pass it
    // would render as raw source characters because we don't run
    // `rehype-raw`. Replace the html node with the same text/link
    // sequence the `text` visitor would produce. A bare opener with no
    // recoverable label leaves the splitter returning `null`, so the
    // html node is left in place (visible) — matching the "repair only
    // the confident cases, leave the rest for telemetry" contract.
    visit(tree, 'html', (node: Html, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnToneBadgeMarkers(node.value);
      if (newChildren === null) return;
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
    // Third pass (PLT-5169): reconstruct paired badges micromark split
    // across SIBLING nodes — `html('<badge …>')`, `text('label')`,
    // `html('</badge>')`. This is the dominant shape in inline-only
    // contexts (table cells especially), where neither per-node pass
    // above can ever see the whole tag in one string, so the raw tag
    // text leaked to the user. Mirrors the PLT-5054 parent pass in
    // `remarkInlineEmphasis` (`reconstructEmphasisFromHtmlNodes`).
    // Visiting every container node and rebuilding its children in place
    // is safe — the pass returns null (no-op) on children without a
    // pairable opener/closer html sequence.
    visit(tree, node => {
      const withChildren = node as unknown as { children?: unknown };
      if (!Array.isArray(withChildren.children)) return;
      const rebuilt = reconstructBadgeFromHtmlNodes(withChildren.children as BadgeReconNode[]);
      if (rebuilt === null) return;
      withChildren.children = rebuilt;
    });
  };
};
